import React from "react";
import { Select } from "antd";
import { useTheme } from "../../context/ThemeContext";
import { StyledSelect } from "./Style";
import { CustomSelectorProps, SelectOption } from "./types";

/**
 * Custom Selector Component
 * A reusable select component built on Ant Design Select
 * 
 * @param value - Selected value
 * @param onChange - Callback when value changes
 * @param options - Array of option objects with value and label
 * @param placeholder - Placeholder text (default: "Select...")
 * @param width - Width in pixels (default: 115)
 * @param height - Height in pixels (default: 38)
 * @param borderRadius - Border radius in pixels (default: 8)
 * @param allowClear - Allow clearing selection (default: false)
 * @param disabled - Disable the select (default: false)
 * @param ...rest - Other Ant Design Select props
 * 
 * @example
 * ```tsx
 * <CustomSelector
 *   value={selectedValue}
 *   onChange={handleChange}
 *   options={[
 *     { value: "1", label: "Option 1" },
 *     { value: "2", label: "Option 2" }
 *   ]}
 *   width={200}
 *   height={40}
 * />
 * ```
 */
const CustomSelector: React.FC<CustomSelectorProps> = ({
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  width = 115,
  height = 38,
  borderRadius = 8,
  allowClear = false,
  disabled = false,
  ...rest
}) => {
  const { isDarkMode } = useTheme();
  const theme = isDarkMode ? "dark" : "light";

  return (
    <StyledSelect
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      width={width}
      height={height}
      borderRadius={borderRadius}
      allowClear={allowClear}
      disabled={disabled}
      theme={theme}
      {...rest}
    >
      {options.map((option: SelectOption) => (
        <Select.Option key={option.value} value={option.value}>
          {option.label}
        </Select.Option>
      ))}
    </StyledSelect>
  );
};

export default CustomSelector;


